/**
 * SsoLanding.js
 *
 * Route: /sso?token=...
 *
 * This page is opened in a new tab when a SynergenHR user clicks
 * "Open Strategizer" in the nav switcher.
 *
 * Flow:
 *  1. Read the SSO token from the URL query param
 *  2. Call POST /sso/login with the token
 *  3a. If linked → store the JWT, redirect to /home
 *  3b. If not linked → show a prompt to link accounts first
 *  3c. If error → show error with link to normal login
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { login } from "../../features/AuthSlice";
import { SsoLoginApi } from "../../api/auth/SsoApi";

function SsoLanding() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState("loading"); // loading | success | not_linked | error
  const [message, setMessage] = useState("");
  const [synergenEmail, setSynergenEmail] = useState("");
  const [synergenUserId, setSynergenUserId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setStatus("error");
      setMessage("No SSO token found in URL. Please try again from SynergenHR.");
      return;
    }

    SsoLoginApi(token).then((res) => {
      if (res.success && res.linked && res.customerAccessToken) {
        // Check if already logged in as a different user
        const currentUser = localStorage.getItem("authUser");
        if (currentUser && currentUser !== res.username) {
          setStatus("mismatch");
          setMessage(
            `You are currently logged into Strategizer as "${currentUser}", but your SynergenHR account is linked to "${res.username}". Please log out first or use the correct SynergenHR account.`
          );
          setSynergenEmail(res.username);
          return;
        }
        // Store the JWT and log in
        localStorage.setItem("authToken", res.customerAccessToken);
        localStorage.setItem("authUser", res.username || "");
        dispatch(login());
        setStatus("success");
        // Brief delay so the user sees the success state, then redirect
        setTimeout(() => navigate("/home", { replace: true }), 800);
      } else if (res.success && !res.linked) {
        // Authenticated via token but no linked account yet
        setStatus("not_linked");
        setSynergenEmail(res.synergenEmail || "");
        setSynergenUserId(res.synergenUserId);
        setMessage(res.message || "");
      } else {
        setStatus("error");
        setMessage(res.message || "SSO login failed. Please try again.");
      }
    });
  }, []);

  const styles = {
    page: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f8fafc",
      fontFamily: "sans-serif",
    },
    card: {
      background: "#fff",
      borderRadius: 12,
      padding: "40px 48px",
      boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      maxWidth: 440,
      width: "100%",
      textAlign: "center",
    },
    title: { fontSize: 22, fontWeight: 700, color: "#1a3a5c", marginBottom: 12 },
    sub: { fontSize: 14, color: "#666", marginBottom: 24, lineHeight: 1.6 },
    btn: {
      display: "inline-block",
      padding: "10px 28px",
      borderRadius: 8,
      background: "#1a3a5c",
      color: "#fff",
      fontWeight: 600,
      fontSize: 14,
      textDecoration: "none",
      cursor: "pointer",
      border: "none",
    },
    spinner: { fontSize: 32, marginBottom: 16 },
  };

  if (status === "loading") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.spinner}>⏳</div>
          <div style={styles.title}>Signing you in…</div>
          <div style={styles.sub}>Verifying your SynergenHR credentials.</div>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.spinner}>✅</div>
          <div style={styles.title}>Logged in!</div>
          <div style={styles.sub}>Redirecting to your dashboard…</div>
        </div>
      </div>
    );
  }

  if (status === "not_linked") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.spinner}>🔗</div>
          <div style={styles.title}>Link your accounts</div>
          <div style={styles.sub}>
            Your SynergenHR account (<strong>{synergenEmail}</strong>) isn't
            linked to a Strategizer account yet.
            <br /><br />
            Log into Strategizer below, then confirm the link.
          </div>
          <a href={`/link?synergen_email=${encodeURIComponent(synergenEmail)}&synergen_user_id=${synergenUserId}`}
             style={styles.btn}>
            Log in &amp; Link Accounts
          </a>
          <div style={{ marginTop: 16 }}>
            <a href="/login" style={{ fontSize: 13, color: "#888" }}>
              Go to normal login
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (status === "mismatch") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.spinner}>⚠️</div>
          <div style={styles.title}>Account Mismatch</div>
          <div style={styles.sub}>{message}</div>
          <button
            style={styles.btn}
            onClick={() => {
              localStorage.removeItem("authToken");
              localStorage.removeItem("authUser");
              window.location.reload();
            }}
          >
            Log out &amp; switch account
          </button>
          <div style={{ marginTop: 12 }}>
            <a href="/home" style={{ fontSize: 13, color: "#888" }}>
              Stay on current account
            </a>
          </div>
        </div>
      </div>
    );
  }

  // error
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.spinner}>❌</div>
        <div style={styles.title}>SSO Failed</div>
        <div style={styles.sub}>{message}</div>
        <a href="/login" style={styles.btn}>Go to Login</a>
      </div>
    </div>
  );
}

export default SsoLanding;
